import { Link } from 'react-router-dom';
import { BenchmarkStatus } from '../../../store/BenchmarkStatus';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { PriceBenchMark } from '../../../store/PriceBenchMark';
import { termLabel, usaCurrency } from '../../../utils/helpers';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { DetailRow } from '../../Compounds/DetailsDrawer/DetailRow';
import { Text } from '../../Atoms/Text';
import { getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';

export interface BenchmarkDetailsProps {
  benchmark: PriceBenchMark;
  pricingOnly?: boolean;
}

export function BenchmarkDetails({ benchmark, pricingOnly = false }: BenchmarkDetailsProps) {
  const state = {
    benchmark,
    prevpath: location.pathname,
    ...getMetaData('inventory')
  };
  return (
    <>
      {!pricingOnly && (
        <DetailViewCard
          enableCollapse
          key="benchmark-details"
          collapsed={false}
          title="Service Details">
          <div className="flex mb-8">
            <Text
              className="font-sans text-grey-4 w-1/2"
              size="sm14">
              {'Address'}
            </Text>
            <Link
              className="w-1/2"
              state={state}
              to={`/inventory/${benchmark.assetId}?detailId=${benchmark.assetId}`}>
              <Text
                className="font-sans font-normal text-indigo"
                size="sm">
                {benchmark.address || benchmark.assetId}
              </Text>
            </Link>
          </div>
          <DetailRow
            key="product"
            colId="product"
            title="Product"
            value={benchmark.product ? `${benchmark.product.family} - ${benchmark.product.name}` : ''}
          />
          <DetailRow
            key="supplier"
            colId="supplier"
            title="Supplier"
            value={benchmark.supplier?.name || ''}
          />
          <DetailRow
            key="speed"
            colId="speed"
            title="Speed"
            value={benchmark.speed || ''}
          />
          <DetailRow
            key="term"
            colId="term"
            title="Term"
            value={termLabel(benchmark.term)}
          />
          <DetailRow
            key="contractEndDate"
            colId="contractEndDate"
            title="Est. Contract End Date"
            value={benchmark.contractEndDate || ''}
          />
        </DetailViewCard>
      )}
      <DetailViewCard
        enableCollapse
        key="pricing-analysis"
        collapsed={false}
        title="Pricing Analysis">
        <DetailRow
          key="mrc"
          colId="mrc"
          title="Current Monthly Cost"
          value={`${usaCurrency.format(benchmark.mrc as number)}`}
        />
        <DetailRow
          key="priceToMarket"
          colId="priceToMarket"
          title="Price to Market"
          value={
            benchmark.benchmarkStatus !== BenchmarkStatus.Available
              ? benchmark.benchmarkStatus === BenchmarkStatus.NotAvailable
                ? 'Not Available'
                : 'Add Needed Data'
              : `${benchmark.priceToMarket >= 0 ? '+' : ''}${usaCurrency.format(benchmark.priceToMarket as number)}`
          }
        />
        <DetailRow
          key="marketLow"
          colId="marketLow"
          title="Low Alternative"
          value={
            benchmark.benchmarkStatus === BenchmarkStatus.Available
              ? benchmark.priceToLowest == null
                ? '-'
                : usaCurrency.format(Number(benchmark.mrc) - Number(benchmark.priceToLowest))
              : benchmark.benchmarkStatus === BenchmarkStatus.NotAvailable
                ? 'Not Available'
                : 'Add Needed Data'
          }
        />
        <DetailRow
          key="marketHigh"
          colId="marketHigh"
          title="High Alternative"
          value={
            benchmark.benchmarkStatus === BenchmarkStatus.Available
              ? benchmark.priceToHighest == null
                ? '-'
                : usaCurrency.format(Number(benchmark.mrc) - Number(benchmark.priceToHighest))
              : benchmark.benchmarkStatus === BenchmarkStatus.NotAvailable
                ? 'Not Available'
                : 'Add Needed Data'
          }
        />
        <div className="mt-4 p-3 bg-grey-2 rounded flex flex-row gap-2 items-start">
          <ErrorOutlineIcon
            fontSize="small"
            className="text-grey-8 mt-0.5 shrink-0"
          />
          <span className="text-grey-8 text-xs leading-relaxed">
            Price to Market, Low and High take into account the details of your services — such as region, product,
            supplier, and cost — and compare them against similar services in our Price Benchmark model.
          </span>
        </div>
      </DetailViewCard>
    </>
  );
}
